import React, { useEffect, useState } from 'react';
import createNote from '../../utils/session/createNote';
import authRequest from '../../utils/authRequest';
import { handleError } from '../../utils/errorHandling';
import { useTranslation } from 'react-i18next';
import { Modal, Tabs, Tag, Row, Col, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import deleteSession from '../../utils/session/deleteSession';
import NoteTypeBuiltInTab from './NoteTypeBuiltInTab';
import NoteTypeTemplatesTab from './NoteTypeTemplatesTab';
import { Grid } from 'antd';
import fetchNoteTemplatesList from '../../utils/note_templates/fetchNoteTemplatesList';
import fetchNoteType from '../../utils/note_types/fetchNoteType';
import NoteTypePreviewPanel from './NoteTypePreviewPanel';
import NoteTemplatePresenter from '../../presenters/NoteTemplatePresenter';
import NoteTemplateUpdateModal from '../preferences/NoteTemplateUpdateModal';
import NoteTemplateNewModal from '../preferences/NoteTemplateNewModal';
import useNoteTemplatePolling from '../../hooks/useNoteTemplatePolling';
import NoteTypeTemplatesGuidance from './NoteTypeTemplatesGuidance';
import deleteNoteTemplate from '../../utils/note_templates/deleteNoteTemplate';

const { useBreakpoint } = Grid;

export default function NoteTypeSelector({ sessionUuid, detailsPage = false, visible, onClose, patient = null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [noteTypes, setNoteTypes] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('built-in');
  const [selectedNoteTypeSlug, setSelectedNoteTypeSlug] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [noteTypePreview, setNoteTypePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [newTemplateModalOpen, setNewTemplateModalOpen] = useState(false);
  const [pollingUuid, setPollingUuid] = useState(null);
  const [isNewTemplateFlow, setIsNewTemplateFlow] = useState(false);
  const { template: polledTemplate } = useNoteTemplatePolling(pollingUuid);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    authRequest('/verticals/1/note_types')
      .then(response => {
        if (parseInt(response.status) === 200) {
          setNoteTypes(response.data);
        } else {
          throw new Error('Error fetching note types');
        }
      })
      .catch(error => {
        handleError(error);
        onClose();
      })
      .finally(() => setLoading(false));
  }, [t, visible, onClose]);

  // Reset selection each time the selector is opened
  useEffect(() => {
    if (visible) {
      setSelectedNoteTypeSlug(null);
      setSelectedTemplate(null);
      setNoteTypePreview(null);
      setActiveTab('built-in');
      setPreviewLoading(false);
      setTemplateModalOpen(false);
      setNewTemplateModalOpen(false);
      setPollingUuid(null);
      setIsNewTemplateFlow(false);
    }
  }, [visible]);

  // When a polled template becomes ready, open update modal
  useEffect(() => {
    if (polledTemplate && polledTemplate.isReady()) {
      setSelectedTemplate(polledTemplate);
      setSelectedNoteTypeSlug(null);
      setNoteTypePreview(null);
      setActiveTab('templates');
      setNewTemplateModalOpen(false);
      setTemplateModalOpen(true);
      setPollingUuid(null);
    }
  }, [polledTemplate]);

  const fetchUserTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const result = await fetchNoteTemplatesList();
      setUserTemplates(result.templates);
    } catch (error) {
      console.error('Error fetching user templates:', error);
      setUserTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const selectNoteType = async (slug) => {
    setSelectedNoteTypeSlug(slug);
    setSelectedTemplate(null);
    setNoteTypePreview(null);
    
    // On mobile, immediately use the selected note type
    if (screens.xs) {
      try {
        await createNote(sessionUuid, slug);
        if (patient) {
          const url = `/patients/${patient.getUUID()}?active_uuid=${sessionUuid}`;
          navigate(url, { state: { reloadSessions: true } });
        } else {
          navigate(`/sessions?active_uuid=${sessionUuid}`, { state: { reloadSessions: true } });
        }
        onClose();
        return;
      } catch (error) {
        Modal.error({
          title: 'Error',
          content: t('common.errors.general'),
        });
        return;
      }
    }
    
    try {
      setPreviewLoading(true);
      const noteType = await fetchNoteType(slug);
      setNoteTypePreview(noteType);
    } catch (error) {
      console.error('Error fetching note type preview:', error);
      setNoteTypePreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setSelectedNoteTypeSlug(null);
    setNoteTypePreview(null);
    
    // On mobile, immediately use the selected template
    if (screens.xs) {
      handleUseClick();
    }
  };

  const handleUseClick = async () => {
    try {
      if (selectedTemplate) {
        await createNote(sessionUuid, 'template', selectedTemplate.getUUID());
      } else if (selectedNoteTypeSlug) {
        await createNote(sessionUuid, selectedNoteTypeSlug);
      } else {
        return;
      }

      if (patient) {
        const url = `/patients/${patient.getUUID()}?active_uuid=${sessionUuid}`;
        navigate(url, { state: { reloadSessions: true } });
      } else {
        navigate(`/sessions?active_uuid=${sessionUuid}`, { state: { reloadSessions: true } });
      }
      onClose();
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: t('common.errors.general'),
      });
    }
  };

  const handleCustomizeClick = () => {
    // Allow customizing either a selected template or a selected note type
    if (selectedTemplate || selectedNoteTypeSlug) {
      setTemplateModalOpen(true);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Reset preview selection when switching tabs
    setSelectedNoteTypeSlug(null);
    setSelectedTemplate(null);
    setNoteTypePreview(null);
    setPreviewLoading(false);
    if (key === 'templates') {
      fetchUserTemplates();
    }
  };

  const switchToStandardTab = () => {
    handleTabChange('built-in');
  };

  const buildTemplateForModal = () => {
    if (selectedTemplate) {
      return selectedTemplate;
    }
    if (selectedNoteTypeSlug) {
      return new NoteTemplatePresenter({
        uuid: 'new-template',
        name: `${t('common.my')} ${t('note_type_selector.note_types.' + selectedNoteTypeSlug + '.title').split(' ').slice(1).join(' ')}`,
        content: (noteTypePreview && noteTypePreview.getMarkdownPreview()) || ''
      });
    }
    return null;
  };
  const handleNewTemplateClick = () => {
    setIsNewTemplateFlow(true);
    setNewTemplateModalOpen(true);
  };

  const handleTemplateCreatedViaUpload = (presenter) => {
    setPollingUuid(presenter.getUUID());
  };

  const handleNewTemplateModalCancel = () => {
    setNewTemplateModalOpen(false);
    setPollingUuid(null);
    // Return to a state with no selection
    setSelectedTemplate(null);
    setSelectedNoteTypeSlug(null);
    setNoteTypePreview(null);
    setPreviewLoading(false);
    setActiveTab('templates');
    setIsNewTemplateFlow(false);
  };

  const handleUpdateModalCancel = () => {
    setTemplateModalOpen(false);
    if (isNewTemplateFlow) {
      // If canceling from the new template flow, clear selection
      setSelectedTemplate(null);
      setSelectedNoteTypeSlug(null);
      setNoteTypePreview(null);
      setPreviewLoading(false);
      setActiveTab('templates');
      setIsNewTemplateFlow(false);
    }
  };

  const handleDeleteTemplateClick = () => {
    if (!selectedTemplate) return;
    Modal.confirm({
      title: t('note_templates.delete_confirm_title'),
      content: t('note_templates.delete_confirm_content'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteNoteTemplate(selectedTemplate.getUUID());
          // Clear selection and refresh list
          setSelectedTemplate(null);
          setNoteTypePreview(null);
          await fetchUserTemplates();
        } catch (error) {
          Modal.error({ title: 'Error', content: t('note_templates.errors.delete') });
        }
      },
    });
  };




  const handleTemplateCreated = (template) => {
    setTemplateModalOpen(false);
    // Refresh the templates list to show the new template
    fetchUserTemplates();
    // Switch to templates tab to show the new template
    setActiveTab('templates');
    // Select the newly created/updated template and clear note type selection
    setSelectedTemplate(template);
    setSelectedNoteTypeSlug(null);
    setNoteTypePreview(null);
    setPreviewLoading(false);
  };

  const handleCancelClick = () => {
    Modal.confirm({
      title: t('note_type_selector.confirm_title'),
      content: t('note_type_selector.confirm_context'),
      okText: t('note_type_selector.confirm_ok_text'),
      okType: 'danger',
      cancelText: t('note_type_selector.confirm_no_text'),
      onOk: async () => {
        const success = await deleteSession(sessionUuid);
        if (success) {
          window.location.reload();
        }
      },
    });
  };

  // Deprecated: previously used to render built-in groups inline; replaced by NoteTypeBuiltInTab

  const renderUserTemplates = () => (
    <NoteTypeTemplatesTab
      templatesLoading={templatesLoading}
      userTemplates={userTemplates}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={selectTemplate}
    />
  );

  const builtInTabContent = (data) => (
    <NoteTypeBuiltInTab
      noteTypes={data}
      selectedNoteTypeSlug={selectedNoteTypeSlug}
      onSelectNoteType={selectNoteType}
      detailsPage={detailsPage}
      onCancelClick={handleCancelClick}
    />
  );

  const items = [
    {
      key: 'built-in',
      label: t('note_type_selector.tabs.standard'),
      children: builtInTabContent(noteTypes),
    },
    {
      key: 'templates',
      label: (
        <span>
          {t('note_type_selector.tabs.templates')}
        </span>
      ),
      children: renderUserTemplates(),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}

      width={screens.xs ? '95vw' : 920}
      destroyOnClose
      maskClosable={false}
      centered
      loading={loading}
      data-testid="note-type-selector"
      closable={detailsPage}
      styles={{
        content: { height: 750 },
        body: { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }
      }}
    >
      <Row gutter={[16, 16]} style={{ height: '100%' }}>
        <Col xs={24} md={screens.xs ? 24 : 10} style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Card 
            styles={{ 
              body: { 
                padding: 0, 
                height: activeTab === 'templates' ? 'auto' : '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                minHeight: 0 
              } 
            }} 
            style={{ flex: activeTab === 'templates' ? 'unset' : 1, border: 'none', boxShadow: 'none' }} 
            bordered={false}
          >
            <Tabs
              activeKey={activeTab}
              items={items}
              onChange={handleTabChange}
              tabBarStyle={{ fontSize: '16px', fontWeight: '500' }}
              style={{ height: activeTab === 'templates' ? 'auto' : '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
            />
          </Card>
          {activeTab === 'templates' && (
            <div style={{ paddingTop: 8, marginTop: 'auto', width: '100%' }}>
              <NoteTypeTemplatesGuidance
                key={selectedTemplate ? selectedTemplate.getUUID() : 'no-selection'}
                onNewTemplate={handleNewTemplateClick}
                onSwitchToStandard={switchToStandardTab}
                collapsed={Boolean(selectedTemplate)}
              />
            </div>
          )}
        </Col>
        {!screens.xs && (
          <Col xs={24} md={14} style={{ height: '100%', display: 'flex', flexDirection: 'column', paddingTop: 16, overflow: 'hidden' }}>
            <NoteTypePreviewPanel
              selectedNoteTypeSlug={selectedNoteTypeSlug}
              selectedTemplate={selectedTemplate}
              noteTypePreview={noteTypePreview}
              previewLoading={previewLoading}
              onUse={handleUseClick}
              onCustomize={handleCustomizeClick}
              isTemplatesTab={activeTab === 'templates'}
              onDeleteTemplate={handleDeleteTemplateClick}
              templatesEmpty={activeTab === 'templates' && !templatesLoading && userTemplates.length === 0}
            />
          </Col>
        )}
      </Row>
      
      <NoteTemplateNewModal
        open={newTemplateModalOpen}
        onCancel={handleNewTemplateModalCancel}
        onTemplateCreated={handleTemplateCreatedViaUpload}
      />

      <NoteTemplateUpdateModal
        open={templateModalOpen}
        onCancel={handleUpdateModalCancel}
        onSave={handleTemplateCreated}
        template={buildTemplateForModal()}
      />
    </Modal>
  );
}
